import { audiencerateApi } from '../../audiencerate';
import { TableResponse } from '../types';
import {
  CampaignResponse,
  CampaignEventV2,
  CampaignProviderStatsV2,
  CampaignStatsV2,
  CampaignV2Response,
  CreateCampaignQueryArgs,
  CreateCampaignRequestBody,
  CreateCampaignV2RequestBody,
  DeleteCampaignArgs,
  GetCampaignArg,
  GetCampaignsArg,
  GetCampaignsCountArg,
  GetCampaignsCountV2Arg,
  GetCampaignEventsV2Arg,
  GetCampaignsV2Arg,
  GetWhatsappTemplateArg,
  GetWhatsappTemplatesArg,
  UpsertWhatsappTemplateRequestBody,
  UpdateCampaignArgs,
  UpdateCampaignRequestBody,
  UpdateCampaignV2RequestBody,
  WhatsappTemplateResponse,
} from './types';

const WHATSAPP_TEMPLATES_BASE_PATH = 'userjourney/whatsapp/templates';

export const campaignsApi = audiencerateApi.injectEndpoints({
  endpoints: (build) => ({
    createCampaign: build.mutation<CampaignResponse, CreateCampaignRequestBody & CreateCampaignQueryArgs>({
      query: ({ workspaceId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    getCampaigns: build.query<TableResponse<CampaignResponse>, GetCampaignsArg>({
      query: ({ workspaceId, limit, offset, searchText: search }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns`,
        params: { limit, offset, search },
      }),
      providesTags: ['Campaigns'],
    }),
    getCampaign: build.query<CampaignResponse, GetCampaignArg>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns/${campaignId}`,
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.campaignId }],
    }),
    getCampaignsCount: build.query<number, GetCampaignsCountArg>({
      query: ({ workspaceId, searchText: search, status }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns/count`,
        params: { search, status },
      }),
      providesTags: ['CampaignsCount'],
    }),
    deleteCampaign: build.mutation<void, DeleteCampaignArgs>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns/${campaignId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaigns'],
    }),
    updateCampaign: build.mutation<void, UpdateCampaignArgs & UpdateCampaignRequestBody>({
      query: ({ workspaceId, campaignId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns/${campaignId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    createCampaignV2: build.mutation<CampaignV2Response, CreateCampaignV2RequestBody & CreateCampaignQueryArgs>({
      query: ({ workspaceId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Campaigns', 'CampaignsCount'],
    }),
    updateCampaignV2: build.mutation<CampaignV2Response, UpdateCampaignArgs & UpdateCampaignV2RequestBody>({
      query: ({ workspaceId, campaignId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Campaigns', 'CampaignsCount'],
    }),
    getCampaignsV2: build.query<TableResponse<CampaignV2Response>, GetCampaignsV2Arg>({
      query: ({ workspaceId, limit, offset, searchText: search, status }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2`,
        params: { limit, offset, search, status },
      }),
      providesTags: ['Campaigns'],
    }),
    getCampaignV2: build.query<CampaignV2Response, GetCampaignArg>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}`,
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.campaignId }],
    }),
    getCampaignsCountV2: build.query<number, GetCampaignsCountV2Arg>({
      query: ({ workspaceId, searchText: search, status }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/count`,
        params: { search, status },
      }),
      providesTags: ['CampaignsCount'],
    }),
    deleteCampaignV2: build.mutation<void, DeleteCampaignArgs>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaigns', 'CampaignsCount'],
    }),
    getCampaignStatsV2: build.query<CampaignStatsV2, GetCampaignArg>({
      query: ({ workspaceId, campaignId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}/stats`,
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.campaignId }],
    }),
    getCampaignEventsV2: build.query<TableResponse<CampaignEventV2>, GetCampaignEventsV2Arg>({
      query: ({ workspaceId, campaignId, limit, offset }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/${campaignId}/events`,
        params: { limit, offset },
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.campaignId }],
    }),
    getCampaignStatsByProviderV2: build.query<CampaignProviderStatsV2[], CreateCampaignQueryArgs>({
      query: ({ workspaceId }) => ({
        url: `/v1/workspaces/${workspaceId}/campaigns-v2/stats/by-provider`,
      }),
      providesTags: ['Campaigns'],
    }),
    getWhatsappTemplates: build.query<WhatsappTemplateResponse[], GetWhatsappTemplatesArg>({
      query: ({ workspaceId, limit, offset, searchText: search }) => ({
        url: `/v1/workspace/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}`,
        params: { limit, offset, search },
      }),
      providesTags: ['Campaigns'],
    }),
    getWhatsappTemplate: build.query<WhatsappTemplateResponse, GetWhatsappTemplateArg>({
      query: ({ workspaceId, templateId }) => ({
        url: `/v1/workspaces/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}/${templateId}`,
      }),
      providesTags: (result, error, args) => [{ type: 'Campaigns', id: args.templateId }],
    }),
    createWhatsappTemplate: build.mutation<WhatsappTemplateResponse, UpsertWhatsappTemplateRequestBody & CreateCampaignQueryArgs>({
      query: ({ workspaceId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    updateWhatsappTemplate: build.mutation<WhatsappTemplateResponse, UpsertWhatsappTemplateRequestBody & GetWhatsappTemplateArg>({
      query: ({ workspaceId, templateId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}/${templateId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    deleteWhatsappTemplate: build.mutation<void, GetWhatsappTemplateArg>({
      query: ({ workspaceId, templateId }) => ({
        url: `/v1/workspaces/${workspaceId}/${WHATSAPP_TEMPLATES_BASE_PATH}/${templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaigns'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateCampaignMutation,
  useGetCampaignsQuery,
  useGetCampaignsCountQuery,
  useLazyGetCampaignsQuery,
  useGetCampaignQuery,
  useDeleteCampaignMutation,
  useUpdateCampaignMutation,
  useCreateCampaignV2Mutation,
  useUpdateCampaignV2Mutation,
  useGetCampaignsV2Query,
  useLazyGetCampaignsV2Query,
  useGetCampaignV2Query,
  useGetCampaignsCountV2Query,
  useDeleteCampaignV2Mutation,
  useGetCampaignStatsV2Query,
  useGetCampaignEventsV2Query,
  useGetCampaignStatsByProviderV2Query,
  useGetWhatsappTemplatesQuery,
  useGetWhatsappTemplateQuery,
  useCreateWhatsappTemplateMutation,
  useUpdateWhatsappTemplateMutation,
  useDeleteWhatsappTemplateMutation,
} = campaignsApi;
